import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Collapse,
  TablePagination
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { motion, AnimatePresence } from 'framer-motion';
import TimelineIcon from '@mui/icons-material/Timeline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalculateIcon from '@mui/icons-material/Calculate';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentIcon from '@mui/icons-material/Payment';
import EnterDetailsManually from './EnterDetailsManually';
import useIsMobile from '../hooks/useIsMobile';

ChartJS.register(ArcElement, ChartTooltip, Legend);
ChartJS.register(ChartDataLabels);

function ViewDeal() {
  const { vin } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useIsMobile();
  
  const [records, setRecords] = useState([]);
  const [error, setError] = useState('');
  const [totalMaintenanceCost, setTotalMaintenanceCost] = useState(0);
  const [purchasePrice, setPurchasePrice] = useState(0);
  const [salePrice, setSalePrice] = useState(0);
  const [yearMakeModel, setYearMakeModel] = useState({ year: '', make: '', model: '' });
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [mileage, setMileage] = useState(0);
  const [color, setColor] = useState('');
  const [purchase_date, setPurchaseDate] = useState('');
  const [sale_type, setSaleType] = useState('');
  const [loading, setLoading] = useState(false);
  const [purchaser, setPurchaser] = useState('');
  const [purchasePriceInput, setPurchasePriceInput] = useState('');
  const [reconditioningInput, setReconditioningInput] = useState('');
  const [salePriceInput, setSalePriceInput] = useState('');
  const [profit, setProfit] = useState(0);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  useEffect(() => {
    const fetchVehicleDetails = async () => {
      setLoading(true);
      try {
        const username = localStorage.getItem('username');
        
        // Fetch vehicle details
        const vehicleResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory?username=${username}`);
        if (!vehicleResponse.ok) {
          throw new Error('Failed to fetch vehicle details');
        }
        
        const vehicleData = await vehicleResponse.json();
        const vehicle = vehicleData.inventory.find(v => v.vin === vin);
        
        if (vehicle) {
          setYearMakeModel({
            year: vehicle.year || '',
            make: vehicle.make || '',
            model: vehicle.model || ''
          });
          setPurchasePrice(parseFloat(vehicle.purchase_price) || 0);
          setSalePrice(parseFloat(vehicle.sale_price) || 0);
          setMileage(vehicle.mileage || 0);
          setColor(vehicle.color || '');
          setPurchaseDate(vehicle.purchase_date || '');
          setSaleType(vehicle.finance_type || '');
          setPurchaser(vehicle.purchaser || '');
        }
        
        // Fetch maintenance records
        await fetchReports();
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchVehicleDetails();
  }, [vin]);

  useEffect(() => {
    setPurchasePriceInput(purchasePrice.toString());
    setReconditioningInput(totalMaintenanceCost.toString());
    setSalePriceInput(salePrice ? salePrice.toString() : '');
  }, [purchasePrice, totalMaintenanceCost, salePrice]);

  useEffect(() => {
    const purchaseValue = parseFloat(purchasePriceInput) || 0;
    const reconditioningValue = parseFloat(reconditioningInput) || 0;
    const saleValue = parseFloat(salePriceInput) || 0;
    
    const investment = purchaseValue + reconditioningValue;
    setTotalInvestment(investment);
    
    const calculatedProfit = saleValue - investment;
    setProfit(calculatedProfit);
  }, [purchasePriceInput, reconditioningInput, salePriceInput]);

  const handleAddExpenseReport = () => {
    setManualEntryOpen(true);
  };

  const handleManualEntryClose = (added) => {
    setManualEntryOpen(false);
    if (added) {
      setTimeout(() => {
        fetchReports();
      }, 300);
    }
  };

  const fetchReports = async () => {
    try {
      const reportsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/reports?vin=${vin}`);
      if (!reportsResponse.ok) {
        throw new Error('Failed to fetch maintenance records');
      }
      const reportsData = await reportsResponse.json();
      setRecords(reportsData.records);
      const total = reportsData.records.reduce((sum, record) => sum + parseFloat(record.cost || 0), 0);
      setTotalMaintenanceCost(total);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditRecord = (recordId) => {
    // Implement edit functionality
    console.log('Edit record:', recordId);
  };

  const handleDeleteRecord = (recordId) => {
    setRecordToDelete(recordId);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/reports/${recordToDelete}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const updatedRecords = records.filter(record => record._id !== recordToDelete);
        setRecords(updatedRecords);
        const total = updatedRecords.reduce((sum, record) => sum + parseFloat(record.cost || 0), 0);
        setTotalMaintenanceCost(total);
      } else {
        throw new Error('Failed to delete record');
      }
    } catch (error) {
      alert('An error occurred while deleting the record');
    } finally {
      setConfirmDeleteOpen(false);
      setRecordToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setRecordToDelete(null);
  };

  const handlePrint = () => {
    setTimeout(() => {
      const chartCanvas = document.querySelector('canvas.chartjs-render-monitor') || 
                         document.querySelector('canvas') ||
                         document.querySelector('.Pie canvas');
      
      let chartImage = null;
      
      if (chartCanvas) {
        try {
          chartImage = chartCanvas.toDataURL('image/png');
        } catch (error) {
          console.error('Error capturing chart:', error);
        }
      }

      const printWindow = window.open('', '', 'width=800,height=600');
      printWindow.document.write(`
        <html>
          <head>
            <title>Maintenance Report</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 40px 50px;
                color: #333;
                line-height: 1.4;
              }
              .header { 
                text-align: center;
                margin-bottom: 20px;
              }
              .header h1 { 
                font-size: 24px; 
                color: #2196F3;
                margin: 0;
                padding-bottom: 5px;
              }
              .header h2 { 
                font-size: 18px;
                margin: 5px 0;
                color: #555;
                text-transform: capitalize;
              }
              .header p {
                margin: 5px 0;
                color: #666;
              }
              .vehicle-details {
                background: #f8f9fa;
                border: 1px solid #e9ecef;
                border-radius: 4px;
                padding: 15px;
                margin-bottom: 20px;
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 10px;
              }
              .vehicle-details p {
                margin: 5px 0;
              }
              .vehicle-details strong {
                color: #444;
              }
              .divider {
                border-bottom: 2px solid #2196F3;
                margin: 10px 0;
              }
              .content-grid {
                display: grid;
                grid-template-columns: 60% 40%;
                gap: 20px;
                margin-bottom: 20px;
              }
              .summary-box {
                background: #f8f9fa;
                border: 1px solid #e9ecef;
                border-radius: 4px;
                padding: 15px;
              }
              .summary-box h3 {
                font-size: 16px;
                margin: 0 0 10px 0;
                color: #444;
              }
              .summary-item {
                display: flex;
                justify-content: space-between;
                margin: 5px 0;
                font-size: 13px;
              }
              .chart-container {
                text-align: center;
              }
              .chart-container h3 {
                font-size: 16px;
                margin: 0 0 10px 0;
                color: #444;
              }
              .chart-image {
                width: 200px;
                height: auto;
              }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-top: 20px;
                font-size: 12px;
              }
              th { 
                background-color: #f0f0f0;
                color: #333;
                font-weight: bold;
                text-align: left;
                padding: 8px;
                border: 1px solid #ddd;
              }
              td { 
                padding: 8px;
                border: 1px solid #ddd;
              }
              tr:nth-child(even) { 
                background-color: #f9f9f9;
              }
              .total-row {
                font-weight: bold;
                background-color: #f0f0f0 !important;
              }
              @page {
                margin: 0.5cm;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Vehicle Maintenance Report</h1>
              <h2>${yearMakeModel.year} ${yearMakeModel.make} ${yearMakeModel.model}</h2>
              <p>VIN: ${vin}</p>
            </div>
            
            <div class="vehicle-details">
              <p><strong>Purchase Price:</strong> $${purchasePrice.toFixed(2)}</p>
              <p><strong>Sale Price:</strong> $${salePrice.toFixed(2)}</p>
              <p><strong>Mileage:</strong> ${mileage}</p>
              <p><strong>Color:</strong> ${color}</p>
              <p><strong>Purchase Date:</strong> ${purchase_date}</p>
              <p><strong>Finance Type:</strong> ${sale_type}</p>
            </div>
            
            <div class="divider"></div>
            
            <div class="content-grid">
              <div class="summary-box">
                <h3>Financial Summary</h3>
                <div class="summary-item">
                  <span>Purchase Price:</span>
                  <span>$${purchasePrice.toFixed(2)}</span>
                </div>
                <div class="summary-item">
                  <span>Total Reconditioning:</span>
                  <span>$${totalMaintenanceCost.toFixed(2)}</span>
                </div>
                <div class="summary-item">
                  <span>Total Investment:</span>
                  <span>$${(purchasePrice + totalMaintenanceCost).toFixed(2)}</span>
                </div>
                ${salePrice > 0 ? `
                <div class="summary-item">
                  <span>Sale Price:</span>
                  <span>$${salePrice.toFixed(2)}</span>
                </div>
                <div class="summary-item">
                  <span>Profit/Loss:</span>
                  <span>$${(salePrice - purchasePrice - totalMaintenanceCost).toFixed(2)}</span>
                </div>
                ` : ''}
              </div>
              
              <div class="chart-container">
                <h3>Cost Breakdown</h3>
                ${chartImage ? `<img src="${chartImage}" class="chart-image" alt="Cost Breakdown Chart" />` : '<p>Chart not available</p>'}
              </div>
            </div>
            
            <h3>Maintenance Records</h3>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Vendor</th>
                  <th>Description</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                ${records.map(record => `
                <tr>
                  <td>${record.date_occurred}</td>
                  <td>${record.category}</td>
                  <td>${record.service_provider}</td>
                  <td>${record.notes}</td>
                  <td>$${parseFloat(record.cost).toFixed(2)}</td>
                </tr>
                `).join('')}
                <tr class="total-row">
                  <td colspan="4" style="text-align: right;">Total</td>
                  <td>$${totalMaintenanceCost.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }, 500);
  };

  // Prepare data for pie chart
  const pieData = {
    labels: ['Purchase Price', 'Reconditioning'],
    datasets: [
      {
        data: [purchasePrice, totalMaintenanceCost],
        backgroundColor: ['#64B5F6', '#FF8A80'],
        hoverBackgroundColor: ['#42A5F5', '#FF5252'],
        borderWidth: 1,
        borderColor: '#fff',
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 15,
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: $${parseFloat(value).toFixed(2)} (${percentage}%)`;
          }
        }
      },
      datalabels: {
        formatter: (value, ctx) => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return `$${parseFloat(value).toFixed(2)}\n${percentage}%`;
        },
        color: '#fff',
        font: {
          weight: 'bold',
          size: 11,
        },
        textAlign: 'center',
      },
    },
  };

  const handleSaveSalePrice = async () => {
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory/${vin}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          purchase_price: parseFloat(purchasePriceInput),
          sale_price: parseFloat(salePriceInput),
          // Add other fields you want to update
        }),
      });
      
      if (response.ok) {
        // Update the local state
        setPurchasePrice(parseFloat(purchasePriceInput));
        setSalePrice(parseFloat(salePriceInput));
        // Show success message or notification
      } else {
        // Handle error
        console.error('Failed to update vehicle');
      }
    } catch (error) {
      console.error('Error updating vehicle:', error);
    }
  };

  const glassmorphismStyle = {
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    boxShadow: theme.shadows[3],
    borderRadius: 2, // Consistent rounding
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Container
        maxWidth="lg"
        sx={{
          py: theme.spacing(4),
          px: { xs: 1, sm: 2, md: 3 },
          // Add a background for the glass effect to be visible
          // Consider moving this to a global style or App.js for consistency
          // backgroundImage: 'url(/path/to/your/background.jpg)', // Example background image
          // background: 'linear-gradient(to bottom right, #f0f4f8, #d9e2ec)', // Example gradient
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: 3, // Optional: round corners for the main container
          mt: 2, // Optional: add some margin top
        }}
      >
        {/* Header */}
        <Box sx={{ mb: theme.spacing(4) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <IconButton
              onClick={() => navigate('/inventory')}
              sx={{
                mr: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) }
              }}
              aria-label="Back to Inventory"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
              {yearMakeModel.year} {yearMakeModel.make} {yearMakeModel.model}
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ pl: { xs: 0, sm: `calc(40px + ${theme.spacing(1.5)})` } }}>
            VIN: {vin}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress size={50} />
          </Box>
        ) : error ? (
          <Paper elevation={3} sx={{ p: 3, textAlign: 'center', bgcolor: 'error.light' }}>
            <Typography color="error.contrastText">{error}</Typography>
          </Paper>
        ) : (
          <>
            {/* Overview Grid */}
            <Grid container spacing={3} sx={{ mb: theme.spacing(4) }}>
              {/* Profit Calculator Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0} // Use elevation 0 with custom shadow in glassmorphismStyle
                  sx={{
                    ...glassmorphismStyle,
                    p: { xs: 2, sm: 3 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 3, fontWeight: 'medium' }}>
                    <CalculateIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                    Financial Overview
                  </Typography>
                  <Stack spacing={2.5} sx={{ flexGrow: 1 }}>
                    <TextField
                      label="Purchase Price"
                      value={purchasePriceInput}
                      onChange={(e) => setPurchasePriceInput(e.target.value)}
                      InputProps={{
                        startAdornment: <Box component="span" sx={{ mr: 0.5 }}>$</Box>,
                        type: 'number'
                      }}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />

                    <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.info.main, 0.1), border: 'none', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                         <MonetizationOnIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                         <Typography variant="body2" color="text.secondary">Reconditioning:</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>${totalMaintenanceCost.toFixed(2)}</Typography>
                    </Paper>

                     <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.secondary.main, 0.1), border: 'none', borderRadius: 1 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center' }}>
                         <TrendingUpIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                         <Typography variant="body2" color="text.secondary">Total Investment:</Typography>
                       </Box>
                       <Typography variant="body1" sx={{ fontWeight: 'medium' }}>${totalInvestment.toFixed(2)}</Typography>
                     </Paper>

                    <TextField
                      label="Sale Price"
                      value={salePriceInput}
                      onChange={(e) => setSalePriceInput(e.target.value)}
                      InputProps={{
                        startAdornment: <Box component="span" sx={{ mr: 0.5 }}>$</Box>,
                        type: 'number'
                      }}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />

                    <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: profit >= 0 ? alpha(theme.palette.success.main, 0.5) : alpha(theme.palette.error.main, 0.5), bgcolor: profit >= 0 ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.error.main, 0.15) }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: profit >= 0 ? 'success.dark' : 'error.dark' }}>Gross Profit:</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: profit >= 0 ? 'success.dark' : 'error.dark' }}>
                          ${profit.toFixed(2)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Stack>
                  {/* <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveSalePrice}
                    fullWidth
                    sx={{ mt: 3, py: 1.2 }}
                    startIcon={<PriceCheckIcon />}
                  >
                    Save Prices
                  </Button> */}
                </Paper>
              </Grid>

              {/* Cost Breakdown Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0} // Use elevation 0 with custom shadow in glassmorphismStyle
                  sx={{
                    ...glassmorphismStyle,
                    p: { xs: 2, sm: 3 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2, fontWeight: 'medium' }}>
                    <TimelineIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                    Cost Breakdown
                  </Typography>
                  <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: { xs: 280, sm: 350 } }}>
                    <Box sx={{ maxWidth: { xs: 280, sm: 350 }, width: '100%' }}>
                      <Pie data={pieData} options={pieOptions} />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddExpenseReport}
                size={isMobile ? "small" : "medium"}
              >
                Add Record
              </Button>
              <Button
                variant="outlined"
                startIcon={<LocalPrintshopIcon />}
                onClick={handlePrint}
                size={isMobile ? "small" : "medium"}
              >
                Print Report
              </Button>
              <Button
                variant="outlined"
                startIcon={<PaymentIcon />}
                onClick={() => navigate(`/payments/${vin}`)}
                size={isMobile ? "small" : "medium"}
                color="secondary"
              >
                Payments
              </Button>
            </Stack>

            {/* Maintenance Records */}
            <Paper
              elevation={0} // Use elevation 0 with custom shadow in glassmorphismStyle
              sx={{
                ...glassmorphismStyle,
                overflow: 'hidden',
                borderRadius: 2,
              }}
            >
              <Box sx={{ p: { xs: 1.5, sm: 2.5 }, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'medium' }}>
                  <ReceiptLongIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                  Maintenance Records
                </Typography>
              </Box>
              <TableContainer>
                <Table size={isMobile ? "small" : "medium"} stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: alpha(theme.palette.grey[500], 0.1), borderBottom: `2px solid ${alpha(theme.palette.divider, 0.3)}`, backdropFilter: 'blur(5px)' } }}>
                      <TableCell sx={{ fontWeight: 'bold', px: { xs: 1, sm: 2 } }}>Date</TableCell>
                      {!isMobile && (
                        <TableCell sx={{ fontWeight: 'bold', px: { xs: 1, sm: 2 } }}>Category</TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 'bold', px: { xs: 1, sm: 2 } }}>{isMobile ? 'Desc' : 'Description'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', px: { xs: 1, sm: 2 } }}>Cost</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '80px', px: { xs: 0.5, sm: 1 } }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <AnimatePresence>
                      {records.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={isMobile ? 4 : 5} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                            No maintenance records found. Add a record to get started.
                          </TableCell>
                        </TableRow>
                      )}
                      {records.map((record, index) => (
                        <motion.tr
                          key={record._id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          style={{ backgroundColor: index % 2 !== 0 ? alpha(theme.palette.grey[500], 0.08) : alpha(theme.palette.common.white, 0.05) }}
                          sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) } }}
                        >
                          <TableCell sx={{ px: { xs: 1, sm: 2 } }}>{record.date_occurred}</TableCell>
                          {!isMobile && (
                            <TableCell sx={{ px: { xs: 1, sm: 2 } }}>{record.category}</TableCell>
                          )}
                          <TableCell sx={{
                            maxWidth: { xs: '100px', sm: '200px' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            px: { xs: 1, sm: 2 }
                          }}>
                            <Tooltip
                              title={
                                `${record.category ? record.category + ': ' : ''}${
                                  typeof record.notes === 'object'
                                    ? (record.notes && record.notes.description
                                        ? record.notes.description
                                        : JSON.stringify(record.notes))
                                    : record.notes
                                }`
                              }
                              placement="top-start"
                            >
                              <span>
                                {typeof record.notes === 'object'
                                  ? (record.notes && record.notes.description
                                      ? record.notes.description
                                      : JSON.stringify(record.notes))
                                  : record.notes}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ px: { xs: 1, sm: 2 }, fontWeight: 'medium' }}>${parseFloat(record.cost).toFixed(2)}</TableCell>
                          <TableCell sx={{ px: { xs: 0.5, sm: 1 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                              <Tooltip title="Edit Record">
                                <IconButton onClick={() => handleEditRecord(record._id)} size="small" sx={{ p: 0.5 }}>
                                  <EditIcon color="primary" fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Record">
                                <IconButton onClick={() => handleDeleteRecord(record._id)} size="small" sx={{ p: 0.5 }}>
                                  <DeleteIcon color="error" fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                    {records.length > 0 && (
                      <TableRow sx={{ '& td': { borderTop: `2px solid ${alpha(theme.palette.divider, 0.3)}`, bgcolor: alpha(theme.palette.grey[500], 0.15) } }}>
                        <TableCell colSpan={isMobile ? 2 : 3} sx={{ fontWeight: 'bold', textAlign: 'right', px: { xs: 1, sm: 2 } }}>
                          Total Maintenance
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', px: { xs: 1, sm: 2 } }}>${totalMaintenanceCost.toFixed(2)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}

        {/* Modal */}
        <Modal
          open={manualEntryOpen}
          onClose={() => setManualEntryOpen(false)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              ...glassmorphismStyle,
              width: { xs: '95%', sm: '600px' },
              maxHeight: '90vh',
              overflow: 'auto',
              borderRadius: '12px',
              p: { xs: 2.5, sm: 4 },
            }}
          >
            <EnterDetailsManually
              open={manualEntryOpen}
              onClose={handleManualEntryClose}
              initialVin={vin}
            />
          </Box>
        </Modal>
        <Dialog
          open={confirmDeleteOpen}
          onClose={handleCancelDelete}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this maintenance record? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </motion.div>
  );
}

export default ViewDeal;